import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { LatLng } from 'leaflet';
import { Form } from '@ui';
import {
  BaseCommercialAviaLegSchemaValues,
  BaseGroundLegSchemaValues,
  BaseSchemaValues,
  CommonLegResourcesSchemaValues,
  LegFormSchema,
  LegFormValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  getCurrentLegFormValues,
  getDefaultLegFormValues,
  getDefaultLegType,
  messagesAddFactory,
  messagesRemoveFactory,
  WarningTypeEnum,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { AuthContext } from '@lp/core/auth';
import { useRouteLegs } from '@lp/features/RoutePlanner/hooks/useRouteLegs';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import { LegTypeEnum, PredefinedPlaceType, RouteLegType, RouteLegTypeEnum, RouteTypeEnum } from '@/api/apis/route';
import { RouteBuilderPermissions } from '@/permissions';
import { CaseStatus, RoutePlanStatus } from '@/api/apis/case';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { isAllowedStatus } from '@/utils/helpers';
import { useWatchFields } from '@/hooks/useWatchFields';
import { RouteFilterType, RouteSegmentType } from '@/types/route';
import { calculateAirTimeDuration, calculateGroundTimeDuration, GROUND_LEG_TYPES } from '@/utils/route';

export enum RouteLegFormMode {
  Create = 'create',
  Edit = 'edit',
}

export type BasicRouteLegFormProps = {
  routeType: RouteTypeEnum;
  currentSegment: RouteSegmentType | null;
  mode: RouteLegFormMode;
  getSegmentLocations: (
    segmentType: string,
    isNewSegment?: boolean,
    includeLocationIds?: string[],
  ) => PredefinedPlaceType[];
  onClose: () => void;
};

export type CreateRouteLegProps = BasicRouteLegFormProps & {
  mode: RouteLegFormMode.Create;
  prevLeg: RouteLegType | null;
};

export type EditRouteLegProps = BasicRouteLegFormProps & {
  mode: RouteLegFormMode.Edit;
  filterType: RouteFilterType;
  currentLeg: RouteLegType;
  onDelete: (legId: LegFormValues) => Promise<void>;
};

export type RouteLegFormProps = CreateRouteLegProps | EditRouteLegProps;

export type LegFormWrapperProps = {
  defaultValues?: Partial<LegFormValues>;
  shouldUnregister?: boolean;
  onSubmit: SubmitHandler<LegFormValues>;
};

export const FormWrapper = ({
  children,
  onSubmit,
  defaultValues = {},
  shouldUnregister = false,
}: PropsWithChildren<LegFormWrapperProps>) => {
  return (
    <Form<LegFormValues>
      defaultValues={defaultValues}
      schema={LegFormSchema}
      onSubmit={onSubmit}
      shouldUnregister={shouldUnregister}
      mode="onSubmit"
      className="flex flex-col max-h-full overflow-auto"
    >
      {children}
    </Form>
  );
};

type LegFormContextType = {
  mode: RouteLegFormMode;
  routeType: RouteTypeEnum;
  initialSegment: RouteSegmentType | null;
  currentLeg: RouteLegType | null;
  previousLeg: RouteLegType | null;
  isNewSegment: boolean;
  isResourcesEdit: boolean;
  isMainDataEdit: boolean;
  isOptionalDataEdit: boolean;
  isRemoveAllowed: boolean;
  warningMessages: string[];
  errorMessages: string[];
  isSubmitEnabled: boolean;
  runSubmitBlockingTransaction: <T>(fn: () => T | Promise<T>) => Promise<void>;
  addErrorMessage: (type: WarningTypeEnum, message?: string) => void;
  removeErrorMessage: (type: WarningTypeEnum | WarningTypeEnum[]) => void;
  addWarningMessage: (type: WarningTypeEnum, message?: string) => void;
  removeWarningMessage: (type: WarningTypeEnum | WarningTypeEnum[]) => void;
  clearErrorsAndWarnings: () => void;
  calculateLegTimeDuration: (
    legType: RouteLegTypeEnum,
    type?: CalculateGroundTimeType,
    timestamp?: string,
  ) => Promise<Record<string, number>>;
  setLegDuration: (formType: LegTypeEnum, legType: RouteLegTypeEnum, data: Record<string, number>) => void;
  getSegmentLocations: (
    segmentType: string,
    isNewSegment?: boolean,
    includeLocationIds?: string[],
  ) => PredefinedPlaceType[];
};

export const initialLegFormContextValues: LegFormContextType = {
  mode: RouteLegFormMode.Create,
  routeType: RouteTypeEnum.Main,
  initialSegment: null,
  currentLeg: null,
  previousLeg: null,
  isNewSegment: true,
  isResourcesEdit: true,
  isMainDataEdit: true,
  isOptionalDataEdit: true,
  isRemoveAllowed: true,
  warningMessages: [],
  errorMessages: [],
  isSubmitEnabled: true,
  runSubmitBlockingTransaction: () => Promise.resolve(),
  addErrorMessage: () => {},
  removeErrorMessage: () => {},
  addWarningMessage: () => {},
  removeWarningMessage: () => {},
  clearErrorsAndWarnings: () => {},
  calculateLegTimeDuration: () => Promise.resolve({}),
  setLegDuration: () => {},
  getSegmentLocations: () => [],
};

export const LegFormContext = createContext<LegFormContextType>(initialLegFormContextValues);

export const LegFormProvider = ({
  routeType,
  currentSegment,
  getSegmentLocations,
  children,
  ...props
}: PropsWithChildren<RouteLegFormProps>) => {
  const { checkPermission } = useContext(AuthContext);
  const { routePlanningStatus, status } = useCaseDetailsWithReviews();
  const [warningMessages, setWarningMessages] = useState<Map<WarningTypeEnum, string>>(new Map());
  const [errorMessages, setErrorMessages] = useState<Map<WarningTypeEnum, string>>(new Map());
  const [isSubmitEnabled, setSubmitEnabled] = useState(true);

  const { setValue, getValues } = useFormContext<CommonLegResourcesSchemaValues | BaseGroundLegSchemaValues>();
  const { legType, sabreGroupId } = useWatchFields<
    BaseSchemaValues & Pick<BaseCommercialAviaLegSchemaValues, 'sabreGroupId'>
  >(['legType', 'sabreGroupId'] as const);

  const isMainDataEdit = useMemo(() => {
    if (props.mode === RouteLegFormMode.Create) {
      return true;
    }

    const isFullFilter = props.filterType === RouteFilterType.Full;
    const hasPermission = checkPermission(RouteBuilderPermissions.LegCreate);
    const isAccommodationLeg = legType === RouteLegTypeEnum.AccommodationLeg;
    const isPlanningStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    const isEditByTypeAllowed = isPlanningStatus || isAccommodationLeg;

    return isFullFilter && hasPermission && isEditByTypeAllowed && isActiveCaseStatus;
  }, [legType, props, checkPermission, routePlanningStatus, status]);

  const isOptionalDataEdit = useMemo(() => {
    if (props.mode === RouteLegFormMode.Create) {
      return true;
    }

    const isGroundLeg = GROUND_LEG_TYPES.includes(legType);
    const isFullFilter = props.filterType === RouteFilterType.Full;
    const hasPermission = isGroundLeg
      ? checkPermission(RouteBuilderPermissions.GroundLegEdit)
      : checkPermission(RouteBuilderPermissions.AirLegEdit);
    const isCorrectStatus = [
      RoutePlanStatus.RoutePlanningStart,
      RoutePlanStatus.PendingResourcesBooking,
      RoutePlanStatus.ResourcesBooked,
    ].includes(routePlanningStatus!);
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return isFullFilter && hasPermission && isCorrectStatus && isActiveCaseStatus;
  }, [legType, status, props, checkPermission, routePlanningStatus]);

  const warningList = useMemo(() => {
    return Array.from(warningMessages.values());
  }, [warningMessages]);

  const errorsList = useMemo(() => {
    return Array.from(errorMessages.values());
  }, [errorMessages]);

  const addWarningMessage = useCallback((type: WarningTypeEnum, message?: string) => {
    messagesAddFactory(setWarningMessages, type, message);
  }, []);

  const removeWarningMessage = useCallback((type: WarningTypeEnum | WarningTypeEnum[]) => {
    messagesRemoveFactory(setWarningMessages, type);
  }, []);

  const addErrorMessage = useCallback((type: WarningTypeEnum, message?: string) => {
    messagesAddFactory(setErrorMessages, type, message);
  }, []);

  const removeErrorMessage = useCallback((type: WarningTypeEnum | WarningTypeEnum[]) => {
    messagesRemoveFactory(setErrorMessages, type);
  }, []);

  const clearErrorsAndWarnings = useCallback(() => {
    setErrorMessages(new Map());
    setWarningMessages(new Map());
  }, []);

  const setLegDuration = useCallback(
    (
      formType: LegTypeEnum,
      currentLegType: RouteLegTypeEnum,
      { timeDurationInMs, distanceInMeters }: Record<string, number>,
    ) => {
      const isFeasible = timeDurationInMs !== -1;
      setValue('timeDurationInMs', isFeasible ? timeDurationInMs : MINUTE_IN_MS);

      if (formType === LegTypeEnum.ground) {
        setValue('distanceInMeters', distanceInMeters);
      }

      const isGroundPartnerLeg = [
        RouteLegTypeEnum.TmdxAffiliateGroundLeg,
        RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
        RouteLegTypeEnum.TxCenterProviderGroundLeg,
        RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
        RouteLegTypeEnum.SelfServicedGroundLeg,
      ].includes(currentLegType);

      if (isGroundPartnerLeg) {
        setValue('isFeasibleForDrive', isFeasible);
      }
    },
    [setValue],
  );

  const calculateLegTimeDuration = useCallback(
    async (
      newLegType: RouteLegTypeEnum,
      type: CalculateGroundTimeType = CalculateGroundTimeType.DepartureTime,
      timestamp?: string,
    ) => {
      const {
        formType: currentFormType,
        departureLocation,
        arrivalLocation,
        departureDateTime,
        arrivalDateTime,
      } = getValues();

      const isAccommodationLeg = currentFormType === LegTypeEnum.accommodation;
      const hasDepartureCoordinates = Boolean(departureLocation?.latitude && departureLocation?.longitude);
      const hasArrivalCoordinates = Boolean(arrivalLocation?.latitude && arrivalLocation?.longitude);
      const isCalculationAllowed = !isAccommodationLeg && hasDepartureCoordinates && hasArrivalCoordinates;

      const fallbackTimestamp = type === CalculateGroundTimeType.DepartureTime ? departureDateTime : arrivalDateTime;
      const currentTimestamp = timestamp || fallbackTimestamp;

      if (isCalculationAllowed) {
        switch (newLegType) {
          case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
          case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
          case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
          case RouteLegTypeEnum.TxCenterProviderGroundLeg:
          case RouteLegTypeEnum.SelfServicedGroundLeg:
            return calculateGroundTimeDuration(
              new LatLng(departureLocation.latitude!, departureLocation.longitude!),
              new LatLng(arrivalLocation.latitude!, arrivalLocation.longitude!),
              type,
              currentTimestamp,
            );
          case RouteLegTypeEnum.TmdxJetAviaLeg:
          case RouteLegTypeEnum.SupplementalLiftAviaLeg:
          case RouteLegTypeEnum.TxCenterProviderAviaLeg:
          case RouteLegTypeEnum.CommercialFlightAviaLeg:
            return calculateAirTimeDuration(
              new LatLng(departureLocation.latitude!, departureLocation.longitude!),
              new LatLng(arrivalLocation.latitude!, arrivalLocation.longitude!),
            );
          default:
            break;
        }
      }

      return { distanceInMeters: 0, timeDurationInMs: MINUTE_IN_MS };
    },
    [getValues],
  );

  const calculateLegTimeDurationOnce = useCallback(async () => {
    const { legType: currentLegType, departureDateTime } = getValues();
    const durationData = await calculateLegTimeDuration(
      currentLegType,
      CalculateGroundTimeType.DepartureTime,
      departureDateTime,
    );

    const isFeasible = durationData.timeDurationInMs !== -1;
    setValue('timeDurationInMs', isFeasible ? durationData.timeDurationInMs : MINUTE_IN_MS);
  }, [getValues, calculateLegTimeDuration, setValue]);

  useEffect(() => {
    if (props.mode === RouteLegFormMode.Edit) {
      calculateLegTimeDurationOnce();
    }
    // Should calculate only on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode]);

  const runSubmitBlockingTransaction = useCallback(async <T,>(fn: () => T | Promise<T>) => {
    setSubmitEnabled(false);

    try {
      await fn();
    } finally {
      setSubmitEnabled(true);
    }
  }, []);

  const previousLeg = useMemo(() => (props.mode === RouteLegFormMode.Create ? props.prevLeg : null), [props]);
  const currentLeg = useMemo(() => (props.mode === RouteLegFormMode.Edit ? props.currentLeg : null), [props]);

  const value = useMemo(
    () => ({
      mode: props.mode,
      routeType,
      initialSegment: currentSegment,
      isNewSegment: !currentSegment?.id,
      currentLeg,
      previousLeg,
      isResourcesEdit: isMainDataEdit,
      isMainDataEdit: isMainDataEdit && !sabreGroupId,
      isRemoveAllowed: props.mode === RouteLegFormMode.Edit && isMainDataEdit,
      isOptionalDataEdit,
      warningMessages: warningList,
      errorMessages: errorsList,
      isSubmitEnabled,
      runSubmitBlockingTransaction,
      addErrorMessage,
      removeErrorMessage,
      addWarningMessage,
      removeWarningMessage,
      setErrorMessages,
      calculateLegTimeDuration,
      setLegDuration,
      getSegmentLocations,
      clearErrorsAndWarnings,
    }),
    [
      props.mode,
      routeType,
      currentSegment,
      currentLeg,
      previousLeg,
      errorsList,
      warningList,
      isMainDataEdit,
      isOptionalDataEdit,
      sabreGroupId,
      isSubmitEnabled,
      runSubmitBlockingTransaction,
      addErrorMessage,
      removeErrorMessage,
      addWarningMessage,
      removeWarningMessage,
      calculateLegTimeDuration,
      setLegDuration,
      getSegmentLocations,
      clearErrorsAndWarnings,
    ],
  );

  return <LegFormContext.Provider value={value}>{children}</LegFormContext.Provider>;
};

export const useLegFormContext = () => {
  const context = useContext(LegFormContext);
  if (context === null) {
    throw new Error('useLegFormContext must be used within a LegFormProvider');
  }
  return context;
};

export const LegFormWrapper = ({
  children,
  onSubmit,
  shouldUnregister = false,
  ...routeLegProviderProps
}: PropsWithChildren<Omit<LegFormWrapperProps, 'defaultValues'> & RouteLegFormProps>) => {
  const { id: logisticsId, isTmdxDriveCompatible } = useLogisticsData();
  const routeLegs = useRouteLegs();

  const defaultLegType = useMemo(() => {
    if (routeLegProviderProps.mode === RouteLegFormMode.Create) {
      return getDefaultLegType({ legs: routeLegs, isTmdxDriveCompatible });
    }

    return routeLegProviderProps.currentLeg.legType;
  }, [routeLegs, routeLegProviderProps, isTmdxDriveCompatible]);

  const defaultValues = useMemo(() => {
    return routeLegProviderProps.mode === RouteLegFormMode.Create
      ? getDefaultLegFormValues({
          currentLegType: defaultLegType,
          segment: routeLegProviderProps.currentSegment,
          logisticsId,
          prevLeg: routeLegProviderProps.prevLeg,
          isNewSegment: !routeLegProviderProps.currentSegment,
          legs: routeLegs,
        })
      : getCurrentLegFormValues({
          currentLeg: routeLegProviderProps.currentLeg,
          segment: routeLegProviderProps.currentSegment,
          logisticsId,
        });
    // Should calculate only on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLegProviderProps.mode]);

  return (
    <FormWrapper defaultValues={defaultValues} onSubmit={onSubmit} shouldUnregister={shouldUnregister}>
      <LegFormProvider {...routeLegProviderProps}>{children}</LegFormProvider>
    </FormWrapper>
  );
};
